import { useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { CustomerResponse } from "../../../types/customer.type";
import { buildLineRegisterUrl } from "../../../utils/lineRegister";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";

interface LineRegisterQrProps {
  customerDetails: CustomerResponse;
}

// Rendered well above the on-screen size so the downloaded PNG still prints sharp
// at 3 × 3 cm and larger; the canvas is scaled down by CSS for display.
const QR_RENDER_PX = 640;
const QR_DISPLAY_PX = 200;
const LABEL_PX = 120;

/**
 * Draws the QR with the shop code and name under it, so a QR stuck up at the shop can
 * be matched to the right shop by eye.
 */
function composePng(qr: HTMLCanvasElement, code: string, name: string): string {
  const canvas = document.createElement("canvas");
  canvas.width = qr.width;
  canvas.height = qr.height + LABEL_PX;
  const ctx = canvas.getContext("2d");
  if (!ctx) return qr.toDataURL("image/png");

  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(qr, 0, 0);
  ctx.fillStyle = "#000000";
  ctx.textAlign = "center";
  ctx.font = "bold 40px 'Prompt', sans-serif";
  ctx.fillText(code, canvas.width / 2, qr.height + 30, canvas.width - 40);
  ctx.font = "32px 'Prompt', sans-serif";
  ctx.fillText(name, canvas.width / 2, qr.height + 80, canvas.width - 40);
  return canvas.toDataURL("image/png");
}

export default function LineRegisterQr({ customerDetails }: LineRegisterQrProps) {
  const qrRef = useRef<HTMLCanvasElement>(null);
  const { uuid, customer_code, name } = customerDetails;

  const handleDownload = () => {
    if (!qrRef.current) return;
    const link = document.createElement("a");
    link.href = composePng(qrRef.current, customer_code, name);
    link.download = `line-qr-${customer_code}.png`;
    link.click();
  };

  return (
    <Paper sx={{ padding: "16px 24px", mt: 3 }}>
      <Typography variant="caption" color="primary" fontWeight={700}>
        QR ลงทะเบียน LINE
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 3, mt: 1 }}>
        {/* Black on white with a 4-module quiet zone, ECC M — the LINE CRM spec's
            minimum for this URL length. */}
        <QRCodeCanvas
          ref={qrRef}
          value={buildLineRegisterUrl(uuid, customer_code)}
          size={QR_RENDER_PX}
          level="M"
          marginSize={4}
          bgColor="#FFFFFF"
          fgColor="#000000"
          style={{ width: QR_DISPLAY_PX, height: QR_DISPLAY_PX }}
        />
        <Box>
          <Typography variant="body2" fontWeight={700}>
            {customer_code}
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {name}
          </Typography>
          <Typography
            variant="caption"
            component="p"
            sx={{ color: (theme) => theme.palette.gray.main, mb: 2 }}
          >
            ให้ลูกค้าสแกนด้วยแอป LINE เพื่อสมัครสมาชิก
            <br />
            พิมพ์ไปติดที่ร้านได้ ขนาดอย่างน้อย 3 × 3 ซม.
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadOutlinedIcon />}
            onClick={handleDownload}
          >
            ดาวน์โหลด PNG
          </Button>
        </Box>
      </Box>
    </Paper>
  );
}
